import type { Workspace } from './workspace';

/**
 * A nook pinned to the head: `pinned: <index>` among its front matter. Only
 * the order of the indices is read, so gaps and negative numbers are fine and
 * absent means unpinned. A new pin takes the front. The app writes and reads
 * the same line.
 */

const FRONT_MATTER = /^---\n[\s\S]*?\n---(?:\n|$)/;
const PINNED_LINE = /^pinned:[ \t]*(-?\d+)[ \t]*$/;

/** The index [frontMatter] pins its nook at, or null when it does not. */
export function pinnedIndex(frontMatter: Record<string, string | string[]>): number | null {
  const raw = frontMatter['pinned'];
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value && /^-?\d+$/.test(value) ? Number.parseInt(value, 10) : null;
}

/** The index a nook pinned now takes: one before the first of [pins], or 0 when there are none. */
export function frontIndex(pins: readonly (number | null)[]): number {
  const indices = pins.filter((i): i is number => i !== null);
  return indices.length === 0 ? 0 : Math.min(...indices) - 1;
}

/** The live pinned nooks in the order the head shows them: by index, then newest sitting, then path.
 *  A nook held inside another is that nook's, pinned or not. */
export function pinnedNooks(workspaces: readonly Workspace[]): Workspace[] {
  return workspaces
    .filter((w) => w.pinned !== null && !w.archived && !w.inside)
    .sort(
      (a, b) =>
        a.pinned! - b.pinned! ||
        (b.sitting ?? '').localeCompare(a.sitting ?? '') ||
        a.path.localeCompare(b.path),
    );
}

/** [raw] with its front matter's lines rewritten by [edit]; the rest is untouched, byte for byte. */
function editFront(raw: string, edit: (lines: string[]) => string[]): string {
  const front = raw.match(FRONT_MATTER);
  if (!front) return raw;
  const closed = front[0].endsWith('\n');
  const inner = front[0].slice(4, front[0].length - (closed ? 5 : 4));
  const lines = edit(inner === '' ? [] : inner.split('\n'));
  return `---\n${lines.length ? `${lines.join('\n')}\n` : ''}---${closed ? '\n' : ''}${raw.slice(front[0].length)}`;
}

/** [raw] pinned at [index], any earlier pin line replaced. */
export function withPinned(raw: string, index: number): string {
  return editFront(raw, (lines) => [...lines.filter((l) => !PINNED_LINE.test(l)), `pinned: ${index}`]);
}

/** [raw] without its pin: an unpinned nook. */
export function withoutPinned(raw: string): string {
  return editFront(raw, (lines) => lines.filter((l) => !PINNED_LINE.test(l)));
}

/**
 * Pins a nook at the front of [pins]. A nook already pinned keeps its index,
 * and a file that changed since is judged as it stands: the change is made
 * again on whatever the file holds when it goes out.
 */
export function pinAtFront(raw: string, pins: readonly (number | null)[]): string {
  const front = raw.match(FRONT_MATTER)?.[0] ?? '';
  if (front.split('\n').some((l) => PINNED_LINE.test(l))) return raw;
  return withPinned(raw, frontIndex(pins));
}
